import {
  Button,
  Card,
  ChannelBadge,
  CardHeader,
  CardTitle,
  EmptyState,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, FileText, Printer, StickyNote, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { downloadAuthenticated } from "../../lib/download";
import { formatDateTime } from "../../lib/format";

export const Route = createFileRoute("/_app/fulfillment")({
  staticData: { layout: "wide" },
  component: FulfillmentPage,
});

const VISIBLE_ORDERS = 4;

function FulfillmentPage() {
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const board = useQuery({
    queryKey: ["fulfillment"],
    queryFn: async () => unwrap(await api.orders.fulfillment.get()),
  });

  const ready = board.data?.readyToShip ?? [];
  const awaitingArrange = board.data?.awaitingArrange ?? [];
  const awaitingInvoice = board.data?.awaitingInvoice ?? [];
  const pick = board.data?.pickList;
  const allIds = useMemo(() => ready.map((row) => row.id), [ready]);
  const hasSelection = selected.size > 0;

  const download = useMutation({
    mutationFn: ({
      orderIds,
      format,
    }: {
      orderIds: string[];
      format: "pdf" | "zpl";
    }) =>
      downloadAuthenticated(
        `/orders/labels?orderIds=${orderIds.join(",")}&format=${format}`,
        format === "zpl" ? "etiquetas-zpl.zip" : "etiquetas.pdf",
      ),
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível baixar as etiquetas",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      }),
  });

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((current) =>
      current.size === allIds.length ? new Set() : new Set(allIds),
    );
  }

  if (board.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const unmappedQty =
    pick?.unmapped.reduce((sum, line) => sum + line.quantity, 0) ?? 0;

  return (
    <>
      <PageHeader
        title="Expedição"
        description={
          pick
            ? `${pick.orderCount} pedidos para separar · ${ready.length} etiquetas prontas`
            : "Lista de separação e etiquetas"
        }
        actions={
          <div className="flex items-center gap-2 print:hidden">
            <Button
              variant="secondary"
              size="sm"
              disabled={!pick || pick.orderCount === 0}
              onClick={() => window.print()}
            >
              <Printer />
              Imprimir lista
            </Button>
            <Menu>
              <MenuTrigger
                render={
                  <Button
                    variant={hasSelection ? "primary" : "secondary"}
                    size="sm"
                    disabled={!hasSelection}
                    loading={download.isPending}
                  >
                    <StickyNote />
                    {hasSelection
                      ? `Etiquetas (${selected.size})`
                      : "Selecione pedidos"}
                    <ChevronDown />
                  </Button>
                }
              />
              <MenuContent align="end">
                <MenuItem
                  onClick={() =>
                    download.mutate({ orderIds: [...selected], format: "pdf" })
                  }
                >
                  <FileText />
                  PDF — impressora comum
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    download.mutate({ orderIds: [...selected], format: "zpl" })
                  }
                >
                  <Printer />
                  ZPL — impressora térmica
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        }
      />

      <div className="grid items-start gap-3 print:grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,26rem)]">
        <Card className="animate-rise">
          <CardHeader>
            <CardTitle className="flex items-center gap-1.5">
              <Truck className="size-3.5 text-ink-muted" />
              Separar
            </CardTitle>
          </CardHeader>
          {!pick ||
          (pick.warehouses.length === 0 && pick.unmapped.length === 0) ? (
            <EmptyState
              icon={<Truck />}
              title="Nada para separar"
              description="Pedidos pagos ainda não enviados aparecem aqui, somados por SKU e depósito. Estoque Full fica de fora."
            />
          ) : (
            <div className="flex flex-col">
              {pick.warehouses.map((warehouse) => (
                <section key={warehouse.warehouseId}>
                  <p className="px-4 pt-4 pb-2 text-[0.8125rem] font-semibold text-ink">
                    {warehouse.warehouseName}
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-16 text-right">Qtd</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right print:hidden">
                          Pedidos
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {warehouse.lines.map((line) => (
                        <TableRow key={line.skuId}>
                          <TableCell className="text-right font-mono text-[0.8125rem] font-semibold text-ink">
                            {line.quantity}
                          </TableCell>
                          <TableCell className="font-mono text-2xs">
                            {line.code}
                          </TableCell>
                          <TableCell className="text-ink">{line.name}</TableCell>
                          <TableCell className="text-right text-2xs text-ink-muted print:hidden">
                            {line.orderCount}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </section>
              ))}
              {pick.unmapped.length > 0 && (
                <section>
                  <div className="flex items-baseline justify-between gap-3 px-4 pt-4 pb-2">
                    <p className="text-[0.8125rem] font-semibold text-ink">
                      Sem vínculo
                      <span className="ml-1.5 font-normal text-ink-muted">
                        {unmappedQty} un. · {pick.unmapped.length}{" "}
                        {pick.unmapped.length === 1 ? "produto" : "produtos"}
                      </span>
                    </p>
                    <Link
                      to="/listings"
                      className="text-2xs font-medium text-ember-strong hover:underline print:hidden"
                    >
                      Vincular em Anúncios
                    </Link>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-16 text-right">Qtd</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead className="print:hidden">Pedidos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pick.unmapped.map((line) => (
                        <TableRow key={line.title}>
                          <TableCell className="text-right font-mono text-[0.8125rem] font-semibold text-ink">
                            {line.quantity}
                          </TableCell>
                          <TableCell className="max-w-md text-ink">
                            <p className="line-clamp-2">{line.title}</p>
                          </TableCell>
                          <TableCell className="print:hidden">
                            <OrderLinks orders={line.orders} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </section>
              )}
            </div>
          )}
        </Card>

        <Card className="animate-rise print:hidden [animation-delay:20ms] lg:sticky lg:top-4 lg:flex lg:max-h-[calc(100dvh-5.5rem)] lg:flex-col lg:overflow-hidden">
          <CardHeader className="shrink-0">
            <CardTitle>Etiquetas prontas</CardTitle>
            <span className="text-2xs text-ink-muted">{ready.length}</span>
          </CardHeader>
          {ready.length === 0 ? (
            <EmptyState
              icon={<StickyNote />}
              title="Nenhuma etiqueta pronta"
              description="Quando o envio estiver pronto para postar, a etiqueta aparece aqui. PDF em qualquer canal; ZPL só no Mercado Livre."
            />
          ) : (
            <div className="min-h-0 flex-1 lg:overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10 px-2">
                      <RowCheck
                        checked={
                          selected.size === ready.length && ready.length > 0
                        }
                        onChange={toggleAll}
                        label="Selecionar todos"
                      />
                    </TableHead>
                    <TableHead>Pedido</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ready.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={selected.has(row.id) ? "selected" : undefined}
                    >
                      <TableCell className="px-2">
                        <RowCheck
                          checked={selected.has(row.id)}
                          onChange={() => toggle(row.id)}
                          label={`Selecionar pedido ${row.externalOrderId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/orders/$id"
                          params={{ id: row.id }}
                          className="font-medium text-ink hover:text-ember"
                        >
                          #{row.externalOrderId}
                        </Link>
                        <p className="mt-0.5 flex items-center gap-2 text-2xs text-ink-muted">
                          <span>{row.buyerName ?? "—"}</span>
                          <ChannelBadge
                            provider={row.provider}
                            nickname={row.accountNickname}
                          />
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-2xs text-ink-faint">
                          {row.itemSummary || formatDateTime(row.placedAt)}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="px-4 py-3 text-2xs text-ink-muted">
                PDF para impressora comum. ZPL existe só no Mercado Livre (zip
                Zebra + romaneio). A Shopee entrega etiqueta em PDF.
              </p>
              {awaitingInvoice.length > 0 ? (
                <div className="border-t border-line px-4 py-3">
                  <p className="text-2xs font-medium text-ink">
                    Aguardando NF-e ({awaitingInvoice.length})
                  </p>
                  <ul className="mt-1 space-y-1 text-2xs text-ink-muted">
                    {awaitingInvoice.map((row) => (
                      <li key={row.id}>
                        <Link to="/orders/$id" params={{ id: row.id }} className="text-ink hover:text-ember">
                          #{row.externalOrderId}
                        </Link>
                        {" · "}
                        envie o XML no pedido antes da etiqueta
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {awaitingArrange.length > 0 ? (
                <div className="border-t border-line px-4 py-3">
                  <p className="text-2xs font-medium text-ink">
                    Preparar envio ({awaitingArrange.length})
                  </p>
                  <ul className="mt-1 space-y-1 text-2xs text-ink-muted">
                    {awaitingArrange.map((row) => (
                      <li key={row.id} className="flex items-center justify-between gap-2">
                        <Link to="/orders/$id" params={{ id: row.id }} className="text-ink hover:text-ember">
                          #{row.externalOrderId}
                        </Link>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            void (async () => {
                              try {
                                unwrap(
                                  await api.orders({ id: row.id })[
                                    "arrange-shipment"
                                  ].post(),
                                );
                                await board.refetch();
                              } catch (error) {
                                toast.add({
                                  type: "error",
                                  title: "Não foi possível preparar o envio",
                                  description:
                                    error instanceof Error
                                      ? error.message
                                      : "Tente de novo em instantes.",
                                });
                              }
                            })();
                          }}
                        >
                          Preparar envio
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function OrderLinks({
  orders,
}: {
  orders: Array<{ orderId: string; externalOrderId: string }>;
}) {
  const visible = orders.slice(0, VISIBLE_ORDERS);
  const hidden = orders.length - visible.length;

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {visible.map((order) => (
        <Link
          key={order.orderId}
          to="/orders/$id"
          params={{ id: order.orderId }}
          className="font-mono text-2xs text-ink-muted hover:text-ember"
        >
          #{order.externalOrderId}
        </Link>
      ))}
      {hidden > 0 && (
        <span className="text-2xs text-ink-faint">+{hidden}</span>
      )}
    </div>
  );
}

function RowCheck({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex size-8 cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-4 accent-ember"
        aria-label={label}
      />
    </label>
  );
}
